import React, {useEffect, useRef, useState} from 'react';
import {useNavigate} from 'react-router-dom';
import {makeStyles} from '@material-ui/core/styles';
import Paper from '@material-ui/core/Paper';
import {AddOutlined, DeleteOutlined, EditOutlined, OpenInNewOutlined} from '@material-ui/icons';
import PageWrapper from '../../../../shared/pagewrapper/PageWrapper.js';
import PageTitle from '../../../../shared/pagetitle/PageTitle.js';
import DataGrid from '../../../../shared/datagrid/DataGrid.tsx';
import BlockUI from '../../../../shared/blockui/BlockUI.tsx';
import ContainedButton from '../../../../shared/buttons/ContainedButton.tsx';
import useConfirmationDialog from '../../../../shared/confirmationdialog/useConfirmationDialog.js';
import {useToast} from '../../../../shared/toast/ToastProvider.tsx';
import {criarColunaOpcoes} from '../../../components/ColunaOpcoes.jsx';
import PageNaoEncontrada from '../../../../PageNaoEncontrada.jsx';
import {ouTraco} from '../../../components/inscricao/documentoInscricao.js';
import {PAGINACAO_PADRAO, textoSemRegistros} from '../../../components/paginacao.js';
import {
    formatarAnoSemestre,
    OPCOES_SITUACAO_INSCRICAO,
    OPCOES_SITUACAO_PROCESSO,
    ROTA_PROCESSOS_SELECAO
} from '../../../components/processo/processoSelecao.js';
import StatusProcessoSelecao from '../../../components/processo/StatusProcessoSelecao.jsx';
import {igualFiltro} from '../../../components/inscricao/situacaoInscricao.js';
import useFetch from '../../../../../hooks/useFetch.js';
import useRemove from '../../../../../hooks/useRemove.js';
import useCheckModule from '../../../../../hooks/useCheckModule.js';
import {URL_PROCESSOS_SELECAO, URL_PROCESSOS_SELECAO_ID} from '../../../../../constants/index.js';
import {buttonTableCell, containerPaper} from '../../../../../utils/styles.js';

const PARAMS_VAZIOS = {};
const FILTER_OPTIONS = {filterByColumn: true};
const TABLE_CONTAINER_PROPS = {component: 'div'};
const COMPARATOR_KEYS = ['id'];

const useStyles = makeStyles(theme => ({
    ...containerPaper(theme),
    colunaCodigo: {
        width: '110px'
    },
    // Cabe o filtro de seleção com o texto da situação inteiro.
    colunaSituacao: {
        minWidth: '170px'
    },
    ...buttonTableCell(theme)
}));

/*
 * Cadastro de Processo de Seleção (processoSeletivo/index.xhtml do PrimeFaces):
 * lista com filtro por coluna, ano/semestre, a situação pelas datas do processo
 * e a situação da inscrição pelo calendário; criação e edição em página própria, exclusão com confirmação e o link
 * da inscrição pública do processo.
 */
export default function PageProcessosSelecao() {
    const classes = useStyles();
    const navigate = useNavigate();
    const checkModule = useCheckModule();
    const permitido = checkModule('PROCSEL');
    const [fetch, carregando] = useFetch({returnInnerLoadingState: true});
    const [remove, removendo] = useRemove({returnInnerLoadingState: true});
    const confirmation = useConfirmationDialog();
    const {createToast} = useToast();
    const [processos, setProcessos] = useState([]);

    // A credencial renova a cada resposta: os callbacks chamam sempre o fetch do render mais recente.
    const fetchRef = useRef(fetch);
    fetchRef.current = fetch;

    const carregar = () => fetchRef.current(URL_PROCESSOS_SELECAO, PARAMS_VAZIOS, dados => setProcessos(Array.isArray(dados) ? dados : []));

    useEffect(() => {
        if (permitido) {
            carregar();
        }
    }, [permitido]);

    const excluir = processo => {
        confirmation('A exclusão de um processo de seleção remove todas as suas configurações. Tem certeza que deseja excluir?', () =>
            remove(URL_PROCESSOS_SELECAO_ID(processo.id), () => {
                createToast({severity: 'success', message: 'Processo de seleção excluído com sucesso!'});
                carregar();
            }));
    };

    /* Mesmo endereço do botão do JSF: a raiz do servidor com ?p={id} abre a inscrição pública do processo. */
    const abrirLinkInscricao = processo => window.open(`/?p=${processo.id}`, '_blank', 'noopener');

    if (!permitido) {
        return <PageNaoEncontrada/>;
    }

    return (
        <PageWrapper>
            <PageTitle title='Processo de Seleção'/>
            <Paper className={classes.containerPaper}>
                <BlockUI loading={carregando || removendo}>
                    <DataGrid
                        rows={processos}
                        comparatorKeys={COMPARATOR_KEYS}
                        filterOptions={FILTER_OPTIONS}
                        paginate
                        paginateOptions={PAGINACAO_PADRAO}
                        tableContainerProps={TABLE_CONTAINER_PROPS}
                        noRecordsText={textoSemRegistros(carregando)}
                        toolbarContent={(
                            <ContainedButton startIcon={<AddOutlined/>} onClick={() => navigate(`${ROTA_PROCESSOS_SELECAO}/novo`)}>
                                Adicionar
                            </ContainedButton>
                        )}
                        columns={[
                            {label: 'Código', field: 'id', filterable: true, className: classes.colunaCodigo, valueGetter: valor => ouTraco(valor)},
                            {label: 'Descrição', field: 'descricao', filterable: true, valueGetter: valor => ouTraco(valor)},
                            {label: 'Programa', field: 'programa.descricao', filterable: true, valueGetter: (valor, row) => ouTraco(row.programa?.descricao)},
                            {
                                label: 'Ano/Semestre', field: 'anoVigente', filterable: true,
                                valueGetter: (valor, row) => ouTraco(formatarAnoSemestre(row.anoVigente, row.semestreReferencia))
                            },
                            {
                                // Pela Data início/término do processo seletivo, calculada no back.
                                label: 'Situação', field: 'situacao', filterable: true, className: classes.colunaSituacao,
                                filterOptions: OPCOES_SITUACAO_PROCESSO,
                                customFilter: (termo, valor) => !!valor && igualFiltro(termo, valor.codigo),
                                valueGetter: valor => <StatusProcessoSelecao situacao={valor}/>
                            },
                            {
                                // Pelo calendário "Inscrição Processo Seletivo": Aberta, Encerrada ou Sem calendário.
                                label: 'Situação da inscrição', field: 'situacaoInscricao', filterable: true, className: classes.colunaSituacao,
                                filterOptions: OPCOES_SITUACAO_INSCRICAO,
                                customFilter: (termo, valor) => !!valor && igualFiltro(termo, valor.codigo),
                                valueGetter: valor => <StatusProcessoSelecao situacao={valor}/>
                            },
                            criarColunaOpcoes(row => [
                                {titulo: 'Editar', icone: <EditOutlined/>, onClick: () => navigate(`${ROTA_PROCESSOS_SELECAO}/${row.id}`)},
                                {titulo: 'Excluir', icone: <DeleteOutlined/>, onClick: () => excluir(row)},
                                {titulo: 'Link da inscrição', icone: <OpenInNewOutlined/>, onClick: () => abrirLinkInscricao(row)}
                            ], {className: classes.buttonTableCell})
                        ]}/>
                </BlockUI>
            </Paper>
        </PageWrapper>
    );
}
